// src/pages/QiuDaoBook.js
import React, { useEffect, useState, useRef, useCallback } from "react";
import TwoColumnLayout from "../layouts/TwoColumnLayout";
import { useHistory } from "react-router-dom";
import PERMISSIONS, { apiFetch, fetchLunarForIntDate } from "../utils/wkCommon";
import {hasUmatPermissionAsync} from "../utils/permissionsAsync";
import { getUser } from "../utils/auth";

const rem = (px) => `${px / 16}rem`;

// helper last day of month (month as "MM" string or number)
function lastDayOfMonth(year, month) {
  if (!month) return 31;
  // month param "01".."12" => convert to number index
  const m = Number(month);
  return new Date(year, m, 0).getDate();
}

// Small helper: check if a string is purely integer digits
function isPureNumber(s) {
  if (s === undefined || s === null) return false;
  return /^\d+$/.test(String(s).trim());
}

function currentAge(qiuAge, qiuYear) {
  const thisYear = new Date().getFullYear();
  if (!qiuAge || !qiuYear) return null;
  if (!/^\d+$/.test(String(qiuAge))) return null; // jika bukan angka murni, dianggap tidak pasti
  return (thisYear - Number(qiuYear) + Number(qiuAge));
}

// Card for Qiu-Dao-Person (presentational)
function CardQiuDaoPerson({ item, highlight = "" }) {
  const a = item.attributes ?? item;
  const gender = (a.gender ?? "").trim();
  const isMale = gender === "乾" || gender === "童" || gender === "男";
  const bgColor = isMale ? "#e0f2fe" /* light-blue */ : "#fee2e2" /* light-red */;

  const ageTextRaw = a.qiuDaoAgeText ?? "";
  const nowAge = currentAge(a.qiuDaoAgeText, a.qiuDaoYear);
  let ageText;
  if (isPureNumber(ageTextRaw)) {
    const rawNum = Number(ageTextRaw);
    const nowNum = Number(nowAge);
    if (!isNaN(nowNum) && rawNum === nowNum) {
      // kalau nilai sama -> tampilkan sekali saja: "XX歲"
      ageText = `${ageTextRaw}歲`;
    } else {
      // normal case: tampilkan "當年 XX歲 ｜ 現年 YY歲" (jika nowAge tersedia)
      ageText = `當年 ${ageTextRaw}歲${ nowAge ? ` ｜ 現年 ${nowAge}歲` : "" }`;
    }
  } else {
    ageText = ageTextRaw;
  }

  // helpers for highlight
  function escapeRegExp(string) {
    return String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  function highlightText(text, term) {
    if (!text) return text || "";
    const t = String(text);
    const q = String(term || "").trim();
    if (!q) return t;

    // split term into tokens (space separated) and highlight any token (works for multi-word search)
    const tokens = q.split(/\s+/).filter(Boolean).map(escapeRegExp);
    if (tokens.length === 0) return t;

    const pattern = new RegExp(tokens.join("|"), "gi");

    let lastIndex = 0;
    const parts = [];
    let match;
    while ((match = pattern.exec(t)) !== null) {
      const idx = match.index;
      if (idx > lastIndex) parts.push(t.slice(lastIndex, idx));
      parts.push(
        <mark key={`${idx}-${match[0]}`} style={{ background: "#fff59d", padding: "0 .08rem", borderRadius: "2px" }}>
          {match[0]}
        </mark>
      );
      lastIndex = pattern.lastIndex;
      if (pattern.lastIndex === idx) pattern.lastIndex++;
    }
    if (lastIndex < t.length) parts.push(t.slice(lastIndex));
    return parts.length ? parts : t;
  }

  const render = (field) => {
    const raw = field ?? "";
    const out = highlightText(raw, highlight);
    return <span>{out}</span>;
  };

  return (
    <div style={{
      border: "1px solid #e5e7eb",
      borderRadius: rem(8),
      padding: rem(12),
      marginBottom: rem(12),
      fontSize: rem(14),
      background: "#fff"
    }}>
      <div style={{ fontSize: rem(16), color: "#111827" }}>
        { render(a.qiuDaoYear ? `${a.qiuDaoYear}` : "") } - { render(a.qiuDaoLocation) }
      </div>

      <div style={{ fontSize: rem(14), color: "#374151" }}>
        { render(a.qiuDaoDate) }
      </div>

      <div style={{
        fontWeight: 700,
        fontSize: rem(16),
        margin: `${rem(6)} 0`,
        background: bgColor,
        padding: rem(6),
        borderRadius: rem(6)
      }}>
        { render(a.qiuDaoName) }{" "}
        <span style={{ paddingLeft: 5, fontWeight: 600, fontSize: rem(15) }}>
          { a.gender
            ? (<>{ "(" }{ render(a.gender) } - { render(ageText) }{ ")" }</>)
            : (<>{ "(" }{ render(ageText) }{ ")" }</>)
          }
        </span>
      </div>

      <div style={{ fontSize: rem(13), color: "#6b7280", marginBottom: rem(6) }}>
        { render(a.phone) } - { render(a.address) }
      </div>

      <div style={{ fontSize: rem(14), color: "#374151" }}>
        { a.yinShi ? (
          <>
            <span style={{ paddingLeft: 0 }}>引師 Yinshi:</span>
            <span style={{ paddingLeft: 5, fontWeight: 500 }}>{ render(a.yinShi) }</span>
            <span style={{ paddingLeft: 10 }}>|</span>
          </>
        ) : null }
        { a.baoShi ? (
          <>
            <span style={{ paddingLeft: 10 }}>保師 Baoshi:</span>
            <span style={{ paddingLeft: 5, fontWeight: 500 }}>{ render(a.baoShi) }</span>
          </>
        ) : null }
      </div>
    </div>
  );
}


export default function QiuDaoBook() {
  const history = useHistory();
  const user = getUser();


  const [canWriteQiuDaoRen, setCanWriteQiuDaoRen] = useState(false);
  const [canFindQiuDaoRen, setCanFindQiuDaoRen] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadPerms() {
      try {
        const email = user?.email;

        const a = await hasUmatPermissionAsync(email, PERMISSIONS.QIUDAOREN_WRITE);
        if (mounted) setCanWriteQiuDaoRen(Boolean(a));
        console.log("canWriteQiuDaoRen:",canWriteQiuDaoRen)

        const b = await hasUmatPermissionAsync(email, PERMISSIONS.QIUDAOREN_FIND);
        if (mounted) setCanFindQiuDaoRen(Boolean(b));
      } catch (e) {
        console.warn("loadPerms failed", e);
      } finally {
        //if (mounted) setPermsReady(true);
      }
    }
    loadPerms();
    return () => { mounted = false; };
  }, [user]);


  // filters
  const thisYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }).map((_, i) => thisYear - 3 + i);
  const monthOptions = [
    { v: "", label: "Bulan" },
    { v: "01", label: "01" }, { v: "02", label: "02" }, { v: "03", label: "03" },
    { v: "04", label: "04" }, { v: "05", label: "05" }, { v: "06", label: "06" },
    { v: "07", label: "07" }, { v: "08", label: "08" }, { v: "09", label: "09" },
    { v: "10", label: "10" }, { v: "11", label: "11" }, { v: "12", label: "12" },
  ];

  const [year, setYear] = useState(thisYear);
  const [month, setMonth] = useState("");
  const [fotangName, setFotangName] = useState("");

  const [bandaos, setBandaos] = useState([]);
  const [bandaosCount, setBandaosCount] = useState(0);
  const [selectedBanDao, setSelectedBanDao] = useState(null);

  const [qiuQuery, setQiuQuery] = useState("");
  const [qiuPeople, setQiuPeople] = useState([]);
  const [qiuCount, setQiuCount] = useState(0);

  const [qiuPage, setQiuPage] = useState(1);
  const qiuPageSize = 25;
  const [hasMoreQiu, setHasMoreQiu] = useState(true);
  const qiuListRef = useRef(null); // ref to right panel scroll container
  const [qiuTotal, setQiuTotal] = useState(null);

  const [loadingBandao, setLoadingBandao] = useState(false);
  const [loadingQiu, setLoadingQiu] = useState(false);

  // uniform input style
  const inputStyle = {
    padding: rem(8),
    fontSize: rem(14),
    borderRadius: rem(6),
    border: "1px solid #d1d5db",
    outline: "none",
    background: "#fff"
  };

  const loadNextQiu = useCallback(() => {
    if (loadingQiu || !hasMoreQiu) return;
    setQiuPage(p => p + 1);
  }, [loadingQiu, hasMoreQiu]);

  useEffect(() => {
    async function fetchBandaoList() {
      setLoadingBandao(true);
      try {
        // Build date range for filters using $gte / $lte (correct for date field)
        const start = `${year}-${month || "01"}-01`;
        const endDay = month ? lastDayOfMonth(year, month) : 31;
        const end = `${year}-${month || "12"}-${String(endDay).padStart(2, "0")}`;

        const params = new URLSearchParams();
        params.set("filters[IntDate][$gte]", start);
        params.set("filters[IntDate][$lte]", end);
        if (fotangName) params.set("filters[fotangName][$containsi]", fotangName);
        // populate qiu relation so we can show counts
        params.set("populate[0]", "qiuDaoPeople");
        params.set("sort", "IntDate:desc");

        const path = `/api/bandaos?${params.toString()}`;
        const json = await apiFetch(path);
        const arr = json?.data ?? [];
        //setBandaos(Array.isArray(arr) ? arr : []);
        // attach lunar info for each bandao (best-effort)
        if (Array.isArray(arr)) {
          const withLunar = await Promise.all(arr.map(async (b) => {
            try {
              const intDate = (b.attributes?.IntDate ?? b.IntDate)?.split?.("T")?.[0] ?? "";
              if (!intDate) return b;
              const norm = String(intDate).split("T")[0]; // -> "2025-11-27"
              const lunar = await fetchLunarForIntDate(norm);
              return { ...b, _lunar: lunar };
            } catch (e) {
              return b;
            }
          }));
          setBandaos(withLunar);
        } else {
          setBandaos([]);
        }

        setBandaosCount(Array.isArray(arr) ? arr.length : 0);
      } catch (err) {
        console.error("fetchBandaoList", err);
        setBandaos([]);
        setBandaosCount(0);
      } finally {
        setLoadingBandao(false);
      }
    }

    fetchBandaoList();
  }, [year, month, fotangName]);

  useEffect(() => {
    // reset paging whenever query or selectedBanDao changes
    setQiuPage(1);
    setHasMoreQiu(true);
    setQiuTotal(null);
    setQiuPeople([]); // clear previous results
  }, [qiuQuery, selectedBanDao]);

  useEffect(() => {
    let cancelled = false;
    async function fetchQiuPage() {
      setLoadingQiu(true);
      try {
        // if a BanDao selected -> show related (no paging)
        if (selectedBanDao) {
          const related = selectedBanDao.qiuDaoPeople ?? [];
          if (!cancelled) {
            setQiuPeople(Array.isArray(related) ? related.map(r => r) : []);
            setQiuCount(Array.isArray(related) ? related.length : 0);
            setHasMoreQiu(false);
          }
          return;
        }

        const q = qiuQuery.trim();
        const isChinese = /[\u3400-\u9FBF]/.test(q);
        if (!q || (!isChinese && q.length < 3)) {
          if (!cancelled) { setQiuPeople([]); setQiuCount(0); setHasMoreQiu(false); }
          return;
        }

        const params = new URLSearchParams();
        params.set("filters[$or][0][qiuDaoName][$containsi]", q);
        params.set("filters[$or][1][yinShi][$containsi]", q);
        params.set("filters[$or][2][baoShi][$containsi]", q);
        params.set("filters[$or][3][address][$containsi]", q);
        params.set("sort", "qiuDaoDate:desc");

        // pagination
        params.set("pagination[page]", String(qiuPage));
        params.set("pagination[pageSize]", String(qiuPageSize));

        const path = `/api/qiu-dao-people?${params.toString()}`;
        const json = await apiFetch(path);

        // Strapi returns data + meta.pagination if configured
        const arr = json?.data ?? [];
        const metaTotal = json?.meta?.pagination?.total ?? null;

        if (cancelled) return;

        if (qiuPage === 1) {
          setQiuPeople(Array.isArray(arr) ? arr : []);
        } else {
          // append (avoid duplicates)
          setQiuPeople(prev => [...prev, ...(Array.isArray(arr) ? arr : [])]);
        }
        setQiuCount(prev => {
          // update count display: if metaTotal exists use it, otherwise use array length
          return metaTotal ?? ((qiuPage === 1) ? (Array.isArray(arr) ? arr.length : 0) : prev + (Array.isArray(arr) ? arr.length : 0));
        });

        setQiuTotal(metaTotal);
        // determine hasMore: if metaTotal known, compare; otherwise if arr.length < pageSize -> no more
        const received = Array.isArray(arr) ? arr.length : 0;
        const more = metaTotal ? ((qiuPage * qiuPageSize) < metaTotal) : (received === qiuPageSize);
        setHasMoreQiu(more);

      } catch (err) {
        console.error("fetchQiu", err);
      } finally {
        if (!cancelled) setLoadingQiu(false);
      }
    }

    fetchQiuPage();
    return () => { cancelled = true; };
  }, [qiuQuery, selectedBanDao, qiuPage]); // note qiuPage dependency to fetch next pages

  function handleSelectBandao(item) {
    setSelectedBanDao(item);
  }

  function handleClearSelection() {
    setSelectedBanDao(null);
    setQiuQuery("");
  }

  function goToCreateBanDao() {
    history.push("/BanDaoForm");
  }

  function goToEditBandao(bandao) {
    const id = bandao?.documentId;
    if (id) history.push(`/BanDaoForm/${id}`);
    else history.push("/BanDaoForm");
  }

  // left panel compact layout
  const leftNode = (
    <div style={{ padding: rem(8) }}>
      <div style={{ display: "flex", gap: rem(8), alignItems: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", minWidth: rem(80) }}>
          <label style={{ fontSize: rem(12), marginBottom: rem(4) }}>Tahun</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ ...inputStyle }}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", minWidth: rem(80) }}>
          <label style={{ fontSize: rem(12), marginBottom: rem(4) }}>Bulan</label>
          <select value={month} onChange={(e) => setMonth(e.target.value)} style={{ ...inputStyle }}>
            {monthOptions.map(m => <option key={m.v} value={m.v}>{m.label}</option>)}
          </select>
        </div>

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <label style={{ fontSize: rem(12), marginBottom: rem(4) }}>Nama Fotang</label>
          <input
            value={fotangName}
            onChange={(e) => setFotangName(e.target.value)}
            placeholder="Nama Fotang"
            style={{ ...inputStyle, width: "80%" }}
          />
        </div>

        <div style={{ display: "none", flexDirection: "column", justifyContent: "flex-end" }}>
          <button
            onClick={() => { setFotangName(""); setMonth(""); setYear(thisYear); }}
            style={{
              padding: `${rem(10)} ${rem(10)}`,
              borderRadius: rem(8),
              fontSize: rem(13),
              border: "1px solid #d1d5db",
              background: "#fff",
              cursor: "pointer"
            }}
            title="Reset filter"
          >
            Reset
          </button>
        </div>
      </div>

      <div style={{ marginTop: rem(10), fontSize: rem(13), color: "#6b7280" }}>
        {loadingBandao ? "Memuat..." : `Hasil: ${bandaosCount} record`}
      </div>

      <div style={{ marginTop: rem(8) }}>
        {bandaos.map(b => (
          <div key={b.documentId} 
              style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "space-between", 
                  marginBottom: rem(8) 
              }}
            >
            <div style={{ 
                    flex: 1, 
                    border: (b.documentId == selectedBanDao?.documentId) ? "2px solid #0b5ed7" : "1px solid #e6e9ee",
                    background: (b.documentId == selectedBanDao?.documentId) ? "#eaf4ff" : "#fff",
                    boxShadow: (b.documentId == selectedBanDao?.documentId) ? "0 6px 18px rgba(11,94,215,0.08)" : "none",
                    cursor: "pointer" 
                  }} 
                  onClick={() => handleSelectBandao(b)}
              >
              <div style={{ border: "1px solid #e5e7eb", borderRadius: rem(8), padding: rem(8) }}>
                <div style={{ fontSize: rem(15) }}>
                  <strong>{(b.IntDate)?.split?.("T")?.[0] ?? ""}</strong>{" "}
                  { b._lunar ? (
                      <span style={{ marginLeft: rem(6), color: "#6b7280", fontSize: rem(13) }}>
                        歲次{b._lunar?.lunarGanZhi ?? ""} {b._lunar?.leapMonth ? "閏" : ""}{b._lunar?.lunarMonth ?? ""}月{b._lunar?.lunarDay ?? ""}日
                      </span>
                  ) : null }
                </div>
                {/* fotang / dcs */}
                <div style={{ fontSize: rem(15), color: "#6b7280" }}>
                  <strong>{b.fotangName}</strong> - {b.Dcs}
                </div>

                {/* gender breakdown (left) + total persons (right) */}
                {(() => {
                  // normalize related persons array (supports .qiuDaoPeople or .qiuDaoPeople.data)
                  const persons = b.qiuDaoPeople ?? [];
                  // count genders robustly
                  const genderCounts = { "乾": 0, "坤": 0, "童": 0, "女": 0 };
                  persons.forEach(p => {
                    let g = (p?.gender ?? "").toString();

                    // mapping khusus
                    if (g === "男") g = "童";

                    if (g && Object.prototype.hasOwnProperty.call(genderCounts, g)) {
                      genderCounts[g] += 1;
                    }
                  });

                  return (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: rem(6) }}>
                      {/* left: show non-zero gender counts (left aligned) */}
                      <div style={{ fontSize: rem(13), color: "#6b7280", textAlign: "left" }}>
                        { (genderCounts["乾"] > 0) && <span style={{ marginRight: rem(8) }}>乾: {genderCounts["乾"]}位</span> }
                        { (genderCounts["坤"] > 0) && <span style={{ marginRight: rem(8) }}>坤: {genderCounts["坤"]}位</span> }
                        { (genderCounts["童"] > 0) && <span style={{ marginRight: rem(8) }}>童: {genderCounts["童"]}位</span> }
                        { (genderCounts["女"] > 0) && <span style={{ marginRight: rem(8) }}>女: {genderCounts["女"]}位</span> }
                      </div>

                      {/* right: total persons (existing behaviour) */}
                      <div style={{ fontSize: rem(13), color: "#6b7280", marginRight: rem(10), textAlign: "right" }}>
                        人數: <strong>{persons.length}位</strong>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            { canWriteQiuDaoRen && (
              <div style={{ marginLeft: rem(8) }}>
                <button onClick={() => goToEditBandao(b)} style={{ padding: `${rem(6)} ${rem(10)}`, borderRadius: rem(8), fontSize: rem(13) }}>
                  Edit
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const rightNode = (
    <>
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: rem(16) }}>求道人名單 Daftar Pemohon Tao</h3>
        <div style={{ fontSize: rem(13), color: "#6b7280", paddingRight:5 }}>{loadingQiu ? "Memuat..." : `Hasil: ${qiuCount}`}</div>
      </div>

      <div style={{ marginTop: rem(8) }}>
        <input
          value={qiuQuery}
          onChange={(e) => setQiuQuery(e.target.value)}
          placeholder={(!selectedBanDao && canFindQiuDaoRen) ? "Cari QiuDaoRen, YinShi, BaoShi atau alamat" : "Search disabled — pilih BanDao untuk lihat related persons"}
          disabled={!canFindQiuDaoRen && !selectedBanDao}
          style={{ width: "97%", ...inputStyle }}
        />
        {selectedBanDao && (
          <div style={{ marginTop: rem(6), fontSize: rem(13), color: "#6b7280" }}>
            Menampilkan yang terkait ke BanDao terpilih.
            <button onClick={handleClearSelection} style={{ marginLeft: rem(8), padding: `${rem(5)} ${rem(8)}`, borderRadius: rem(6) }}>Batal pilih</button>
          </div>
        )}
      </div>
    </div>
    <div
      ref={qiuListRef}
      onScroll={(e) => {
        const el = e.target;
        // when within 200px from bottom -> load next
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
          loadNextQiu();
        }
      }}
      style={{ padding: rem(8), height: "70vh", overflow: "auto" }} // set a scrollable height
    >
      <div style={{ marginTop: rem(12) }}>
        {qiuPeople.map(q => <CardQiuDaoPerson key={q.documentId} item={q} highlight={ selectedBanDao ? "" : qiuQuery } />)}
        {loadingQiu && qiuPage > 1 && <div style={{ textAlign: "center", padding: rem(8), color: "#6b7280" }}>Memuat lebih banyak...</div>}
        {!hasMoreQiu && qiuPeople.length > 0 && <div style={{ textAlign: "center", padding: rem(8), color: "#6b7280" }}>Tidak ada lagi</div>}
      </div>
    </div>
    </>
  );

  const headerActions = (
    <div style={{ display: "flex", gap: rem(8), alignItems: "center" }}>
      {canWriteQiuDaoRen && (
        <button
          onClick={goToCreateBanDao}
          title="Catat BanDao"
          style={{
            width: rem(34),
            height: rem(34),
            borderRadius: rem(8),
            fontSize: rem(18),
            background: "#0b5ed7",
            color: "#fff",
            border: "none",
            cursor: "pointer",
          }}
        >
          +
        </button>
      )}
    </div>
  );

  return (
    <TwoColumnLayout
      leftNode={leftNode}
      rightNode={rightNode}
      title="求道紀錄簿 Buku QiuDao"
      headerActions={headerActions}
    />
  );
}
